/**
 * TemplateSelector Component
 *
 * Dropdown for choosing a pre-approved WhatsApp template.
 * Shows approval status and language for each template.
 */

import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  FileText,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import type {
  WhatsAppTemplate,
  TemplateApprovalStatus,
} from "../types";

interface TemplateSelectorProps {
  templates: WhatsAppTemplate[];
  selectedTemplate: WhatsAppTemplate | null;
  loading: boolean;
  onSelect: (templateId: string) => void;
  onOpenAIGenerator?: () => void;
}

/* ─── Status metadata ─── */
const STATUS_META: Record<
  TemplateApprovalStatus,
  { icon: typeof CheckCircle2; color: string; label: string }
> = {
  approved: { icon: CheckCircle2, color: "text-green-500", label: "Aprobado" },
  pending: { icon: Clock, color: "text-amber-500", label: "Pendiente" },
  rejected: { icon: XCircle, color: "text-red-500", label: "Rechazado" },
};

export function TemplateSelector({
  templates,
  selectedTemplate,
  loading,
  onSelect,
  onOpenAIGenerator,
}: TemplateSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedStatus =
    selectedTemplate && STATUS_META[selectedTemplate.approvalStatus];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} className="text-emerald-500" />
        <h3 className="font-semibold text-slate-900">Elegir plantilla</h3>
        <Link
          to="/whatsapp/templates"
          className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 ml-auto"
        >
          Gestionar plantillas
          <ExternalLink size={10} />
        </Link>
      </div>

      {/* Dropdown trigger */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => !loading && setIsOpen(!isOpen)}
          disabled={loading}
          className={`
            w-full flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all duration-200
            ${
              isOpen
                ? "border-emerald-300 ring-2 ring-emerald-100 bg-emerald-50/30"
                : "border-slate-200 hover:border-slate-300 bg-white"
            }
            ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
          `}
        >
          <span
            className={
              selectedTemplate ? "text-slate-900 font-medium" : "text-slate-400"
            }
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Cargando templates...
              </span>
            ) : selectedTemplate ? (
              <span className="flex items-center gap-2">
                {selectedStatus && (
                  <selectedStatus.icon
                    size={14}
                    className={selectedStatus.color}
                  />
                )}
                {selectedTemplate.name}
              </span>
            ) : (
              "Seleccionar plantilla aprobada"
            )}
          </span>
          <ChevronDown
            size={18}
            className={`text-slate-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-lg z-30 overflow-hidden animate-fade-in">
            <div className="max-h-80 overflow-y-auto">
              {templates.map((tpl) => {
                const Status = STATUS_META[tpl.approvalStatus];
                const isDisabled = tpl.approvalStatus !== "approved";
                const isSelected = tpl.id === selectedTemplate?.id;

                return (
                  <button
                    key={tpl.id}
                    onClick={() => {
                      if (isDisabled) return;
                      onSelect(tpl.id);
                      setIsOpen(false);
                    }}
                    disabled={isDisabled}
                    className={`
                      w-full flex items-start gap-3 px-4 py-3 text-left transition-colors duration-150 border-b border-slate-50 last:border-b-0
                      ${isSelected ? "bg-emerald-50" : ""}
                      ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-slate-50 cursor-pointer"
                      }
                    `}
                  >
                    <Status.icon
                      size={16}
                      className={`${Status.color} mt-0.5 flex-shrink-0`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`font-medium text-sm ${
                            isSelected ? "text-emerald-700" : "text-slate-900"
                          }`}
                        >
                          {tpl.name}
                        </span>
                        <span className="text-[10px] text-slate-400 ml-auto font-mono">
                          {tpl.language}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2 leading-snug">
                        {tpl.body}
                      </p>
                      {tpl.variableCount > 0 && (
                        <p className="text-[10px] text-slate-400 mt-1">
                          {tpl.variableCount}{" "}
                          {tpl.variableCount === 1 ? "variable" : "variables"} ·
                          ${tpl.estimatedCostUSD.toFixed(4)} / conversación
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer: shortcuts to create new templates */}
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onOpenAIGenerator?.();
                }}
                className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium"
              >
                <Sparkles size={11} />
                Crear con IA
              </button>
              <Link
                to="/whatsapp/templates/new"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
              >
                <FileText size={11} />
                Crear manual →
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Selected template info card */}
      {selectedTemplate && (
        <div className="mt-4 p-4 bg-emerald-50/50 rounded-xl border border-emerald-100 animate-fade-in">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText size={16} className="text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {selectedTemplate.name}
                </p>
                {selectedStatus && (
                  <span
                    className={`flex items-center gap-1 text-[10px] font-medium ${selectedStatus.color}`}
                  >
                    <selectedStatus.icon size={11} />
                    {selectedStatus.label}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {selectedTemplate.variableCount} variables · idioma{" "}
                <span className="font-mono">{selectedTemplate.language}</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateSelector;
